import { useCallback, useRef } from 'react'
import AppStore from '@v4/store/appStore'
import { useSignUpData, useQueryInitialAnswers, useResultInitialAnswers } from '@v4/components/common/Form'
import { formatAnalyticData, formatErrors, formatValues } from '@v4/components/common/Form/Form.utils'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import PageData from '@v4/utils/data/PageData'
import { validateEmailAdvance } from '@v4/utils/validations'

export const useSignUpForm = props => {
	const {
		className,
		classes,
		buttonText,
		title,
		titleConfig,
		titleEditable,
		placeholderColor,
		usePaperColors,
		fieldsPerRow,
		onSuccess,
		onTitleClick,
		onButtonClick,
		onTitleChange,
		buttonTextEditable,
		buttonTextConfig,
		onButtonTextChange,
		titleKey,
		buttonTextKey,
		rounded,
		mobileAlignment,
		white,
		image,
		onImageClick,
	} = props
	const captchaRef = useRef(null)
	const { singUp, completeSingUp } = AppStore.useStoreActions(actions => actions.signUp)
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const pageType = PageBuilderSelector.useType()
	const pageId = PageBuilderSelector.usePageId()
	const { reset: resetPage } = PageBuilderSelector.useActions()
	const queryInitialAnswers = useQueryInitialAnswers()
	const resultInitialAnswers = useResultInitialAnswers()
	const isResult = PageData.isTypeResult(pageType)
	const { data, settings, formikProps } = useSignUpData(isResult ? resultInitialAnswers : queryInitialAnswers)

	const advancedEmailValidation = settings.advanced_email_validation

	const handleFormSubmit = useCallback(
		async (values, formikBag) => {
			if (isBuilderMode) return {}
			let captcha
			const emailError = await validateEmailAdvance(values.email, advancedEmailValidation)

			if (emailError) {
				formikBag.setErrors({ email: emailError })
				return undefined
			}
			if (captchaRef.current) {
				try {
					const { response } = await captchaRef.current.execute({ async: true })
					captcha = response
				} catch (error) {
					formikBag.setErrors(formatErrors(new Error('Captcha verification failed')))
					return undefined
				}
			}

			const submit = isResult
				? (...args) =>
						completeSingUp(...args).then(response => {
							if (response.page) {
								window.initialData.page = response.page
								resetPage()
							}
							return response
						})
				: singUp

			const formValues = formatValues(values)
			const analyticData = formatAnalyticData(formValues, data)

			return submit({ pageId, values: { ...(captcha && { captcha }), ...formValues }, analyticData })
				.then(onSuccess)
				.catch(err => {
					formikBag.setErrors(formatErrors(err))
				})
		},
		[isBuilderMode, advancedEmailValidation, isResult, completeSingUp, singUp, data, pageId, onSuccess, resetPage],
	)

	return [
		{
			captchaRef,
			classes,
			data,
			settings,
			mobileAlignment,
			formikProps,
			className,
			buttonText,
			buttonTextEditable,
			buttonTextConfig,
			title,
			titleConfig,
			placeholderColor,
			usePaperColors,
			fieldsPerRow,
			titleEditable,
			titleKey,
			buttonTextKey,
			rounded,
			white,
			image,
			withCaptcha: !isResult,
		},
		{
			handleFormSubmit,
			handleTitleClick: onTitleClick,
			handleButtonClick: onButtonClick,
			handleTitleChange: onTitleChange,
			handleButtonTextChange: onButtonTextChange,
			handleImageClick: onImageClick,
		},
	]
}
